import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import countriesData from '@resideo/blueprint-react/dist/components/CountryStateSelect/countries.json';
import { getStates } from 'utils/stateUtils';

type CountryStateOption = {
  value: string;
  text: string;
};

type CountryStateSelectValue = string | CountryStateOption[];

const useCountryStateSelector = () => {
  const { t } = useTranslation('blueprint');
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [statesList, setStatesList] = useState<CountryStateOption[]>([]);
  const statesCacheRef = useRef<Record<string, CountryStateOption[]>>({});
  const stateChangeHandlerRef = useRef<((value: string) => void) | null>(null);

  const countriesList = useMemo(
    () =>
      [...countriesData]
        .map(c => ({
          value: c.iso2,
          text: t(`countries.${c.iso2}`),
        }))
        .sort((left, right) => left.text.localeCompare(right.text)),
    [t]
  );

  const updateCountry = (value: CountryStateSelectValue) => {
    const nextCountry = typeof value === 'string' ? value : '';
    const hasCountryChanged = nextCountry !== country;
    setCountry(nextCountry);
    if (hasCountryChanged) {
      setState('');
      stateChangeHandlerRef.current?.('');
    }
  };

  const updateState = (value: CountryStateSelectValue) => {
    setState(typeof value === 'string' ? value : '');
  };

  useEffect(() => {
    if (!country || typeof country !== 'string') {
      setStatesList([]);
      return;
    }

    if (statesCacheRef.current[country]) {
      setStatesList(statesCacheRef.current[country]);
      return;
    }

    let cancelled = false;
    setStatesList([]);

    getStates(country).then(result => {
      if (cancelled) {
        return;
      }

      const formattedStates = [...result]
        .sort((left, right) => left.name.localeCompare(right.name))
        .map(stateOption => ({
          value: stateOption.iso2,
          text: stateOption.name,
        }));

      statesCacheRef.current[country] = formattedStates;
      setStatesList(formattedStates);
    });

    return () => {
      cancelled = true;
    };
  }, [country]);

  return useMemo(
    () => ({
      country,
      setCountry: updateCountry,
      state,
      setState: updateState,
      countriesList,
      statesList,
      stateChangeHandlerRef,
      isStateFieldEnabled: statesList.length > 0,
    }),
    [country, state, statesList, countriesList]
  );
};

export default useCountryStateSelector;
